import {
  MAX_STAGE_COLORS,
  resolveStorefrontTokens,
  STOREFRONT_RARITY_TIERS,
  STOREFRONT_THEME_PRESETS,
  STOREFRONT_TOKEN_GROUPS,
  type StorefrontTheme,
  type StorefrontTokenKey,
} from "@loot-os/api-contract";
import { Badge } from "@loot-os/ui/components/badge";
import { Button } from "@loot-os/ui/components/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@loot-os/ui/components/collapsible";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@loot-os/ui/components/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@loot-os/ui/components/dropdown-menu";
import { Label } from "@loot-os/ui/components/label";
import { toast } from "@loot-os/ui/components/sonner";
import { Textarea } from "@loot-os/ui/components/textarea";
import { cn } from "@loot-os/ui/lib/utils";
import { ChevronRight, Ellipsis } from "lucide-react";
import { type ReactNode, useMemo, useState } from "react";

import { useStorefrontConfig } from "../lib/storefront";
import {
  choiceValue,
  countSemanticOverrides,
  countThemeChanges,
  listThemeChanges,
  matchedPreset,
  parseThemeJson,
  SEMANTIC_COLOR_GROUPS,
  stylePaths,
  styleSummary,
  THEME_STYLES,
  type ThemeStyle,
  themeToJson,
  toSavedTheme,
  withChoice,
  withFieldReverted,
  withoutSemanticOverrides,
  withSemanticColor,
  withStageColor,
  withStylePicked,
} from "../lib/storefrontTheme";
import { useTenantRole } from "../lib/useTenantRole";
import { useThemePreviewInputs } from "../lib/useThemePreviewInputs";
import { useUnsavedChangesGuard } from "../lib/useUnsavedChangesGuard";
import { ChoiceField, ColorField, FontField, RadiusField } from "./StorefrontFields";
import { StorefrontPaletteGenerator } from "./StorefrontPaletteGenerator";
import { ThemePreviewPanel } from "./ThemePreviewPanel";
import { UnsavedChangesDialog } from "./UnsavedChangesDialog";

/**
 * The Theme tab of the Storefront page: how the store looks, on one page beside a live preview.
 * Three cards: Colors (the palette generator, then the worked-out colors and the rarity colors,
 * collapsed), Type (the three fonts and the Headings choice) and Style. The choices
 * (`THEME_STYLES`: corners, surfaces, headings, energy, icons, backdrop) are each made in a word
 * and open onto the fields they set; one whose fields match none of its words reads Custom. How the store is put
 * together (header, footer, chat) is the Pages page's. A look is a starting point applied to the
 * draft, not something the theme is measured against: every change counts against the saved
 * theme, in the bar at the bottom, whose Review lists each one with an Undo.
 *
 * The draft starts as the whole stored theme and the column is written whole, so a save sends
 * back every group, including any this page has no control for (`toSavedTheme`). Collapsed
 * groups stay mounted (the generator's seeds live in it). It runs its own `useStorefrontConfig`,
 * so a save from another tab never fires its `onSaved` and drops a draft in progress. A
 * storefront section, so `StorefrontLayout` has loaded the config before this mounts. Leaving
 * with unsaved changes asks first (`useUnsavedChangesGuard`).
 */
export function StorefrontThemeEditor() {
  const [draft, setDraft] = useState<StorefrontTheme | null>(null);
  // Remounts the generator on save and discard, so its seed fields start blank again.
  const [generation, setGeneration] = useState(0);
  const [reviewing, setReviewing] = useState(false);
  const [jsonOpen, setJsonOpen] = useState(false);
  const reset = () => {
    setDraft(null);
    setReviewing(false);
    setGeneration((n) => n + 1);
  };
  const { query, update } = useStorefrontConfig({ onSaved: reset });
  const { can } = useTenantRole();
  const [showPreview, setShowPreview] = useState(true);

  // Above the narrowing below, as hooks must be. What a save would store, memoized so the
  // preview posts only when it changes.
  const stored = query.data?.config;
  const draftTheme = draft ?? stored?.theme;
  const savedDraft = useMemo(() => toSavedTheme(draftTheme ?? {}), [draftTheme]);
  const previewInputs = useThemePreviewInputs();
  const guard = useUnsavedChangesGuard({
    dirty: stored !== undefined && draft !== null && countThemeChanges(stored.theme, draft) > 0,
    // Not while a save is already in flight: the column is written whole with no version
    // check, so a second write could land first and be overwritten by the older one.
    onSave:
      draft && can("editor") && !update.isPending
        ? () => update.mutateAsync({ theme: toSavedTheme(draft) })
        : undefined,
    onDiscard: reset,
  });

  // Narrowing only: the layout renders a section once the same query has data.
  if (!query.data) return null;

  const { config } = query.data;
  const theme: StorefrontTheme = draft ?? config.theme;
  const tokens = (theme.tokens ?? {}) as Partial<Record<StorefrontTokenKey, string>>;
  const palette = resolveStorefrontTokens(tokens);
  const canEdit = can("editor");
  const changes = listThemeChanges(config.theme, theme);
  const overrides = countSemanticOverrides(theme);
  // The overrides only matter once the palette moves away from what they were set against.
  const paletteChanged = listThemeChanges({ tokens: config.theme.tokens }, { tokens }).length > 0;

  const setToken = (key: StorefrontTokenKey, value: string) =>
    setDraft({ ...theme, tokens: { ...tokens, [key]: value } });

  const save = (e: React.FormEvent) => {
    e.preventDefault();
    update.mutate({ theme: toSavedTheme(theme) });
  };

  return (
    <div
      className={cn(
        "grid items-start gap-8",
        showPreview && "xl:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]",
      )}
    >
      {/* Beside the editor on a wide screen, held in view while it scrolls; above it on a
          narrower one. */}
      {showPreview && (
        <ThemePreviewPanel
          {...previewInputs}
          theme={savedDraft}
          className="h-128 xl:sticky xl:top-4 xl:order-last xl:h-[calc(100vh-2rem)]"
        />
      )}
      <UnsavedChangesDialog guard={guard} />
      <form className="@container flex min-w-0 flex-col gap-6" onSubmit={save}>
        <div className="flex flex-wrap items-center gap-2">
          <p className="mr-auto min-w-0 flex-1 basis-60 text-sm text-muted-foreground">
            How your store looks, everywhere. Open any choice to see exactly what it sets.
          </p>
          <DropdownMenu>
            <DropdownMenuTrigger
              render={<Button type="button" variant="outline" size="sm" disabled={!canEdit} />}
            >
              Start from a look
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-72">
              <DropdownMenuGroup>
                <DropdownMenuLabel>
                  Replaces your colors. Review it in the bar below, then save.
                </DropdownMenuLabel>
              </DropdownMenuGroup>
              <DropdownMenuSeparator />
              {STOREFRONT_THEME_PRESETS.map((preset) => (
                <DropdownMenuItem
                  key={preset.id}
                  onClick={() => {
                    setDraft({ ...theme, tokens: { ...preset.tokens } });
                    setGeneration((n) => n + 1);
                  }}
                  className="items-start gap-3"
                >
                  <span className="mt-0.5 flex shrink-0 -space-x-1">
                    {(["background", "card", "primary"] as const).map((token) => (
                      <span
                        key={token}
                        className="size-3.5 rounded-full ring-1 ring-border"
                        style={{ backgroundColor: preset.tokens[token] }}
                      />
                    ))}
                  </span>
                  <span className="grid gap-0.5">
                    <span className="font-medium">{preset.name}</span>
                    <span className="text-xs text-muted-foreground">{preset.description}</span>
                  </span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <DropdownMenu>
            <DropdownMenuTrigger
              render={<Button type="button" variant="outline" size="icon-sm" aria-label="More" />}
            >
              <Ellipsis />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setJsonOpen(true)}>
                Import or export…
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setShowPreview((shown) => !shown)}>
                {showPreview ? "Hide preview" : "Show preview"}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <Card
          title="Colors"
          description="Pick two or three; every other color is worked out from them."
        >
          <StorefrontPaletteGenerator
            key={generation}
            current={palette}
            radius={theme.radius?.trim() || undefined}
            fontFamily={theme.fontFamily?.trim() || undefined}
            headingFontFamily={theme.headingFontFamily?.trim() || undefined}
            canEdit={canEdit}
            onGenerate={(generated) => setDraft({ ...theme, tokens: generated })}
          />
          <div className="grid divide-y border-t">
            <Disclosure
              title="Worked-out colors"
              summary={`Worked out from your brand · ${overrides} set by hand`}
            >
              {overrides > 0 && paletteChanged && (
                <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-dashed px-4 py-3 text-sm">
                  <span className="text-muted-foreground">
                    {overrides === 1
                      ? "1 color set by hand keeps its own value"
                      : `${overrides} colors set by hand keep their own values`}{" "}
                    and won't follow your brand colors.
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="ml-auto"
                    onClick={() => setDraft(withoutSemanticOverrides(theme))}
                  >
                    Clear them
                  </Button>
                </div>
              )}
              <div className="grid gap-6">
                {STOREFRONT_TOKEN_GROUPS.map((group) => (
                  <ColorGroup key={group.label} title={group.label} description={group.description}>
                    {group.tokens.map((token) => (
                      <ColorField
                        key={token.key}
                        id={`theme-token-${token.key}`}
                        label={token.label}
                        hint={`--${token.key}`}
                        fallback={token.default}
                        value={tokens[token.key] ?? ""}
                        onChange={(value) => setToken(token.key, value)}
                      />
                    ))}
                  </ColorGroup>
                ))}
                {SEMANTIC_COLOR_GROUPS.map((group) => (
                  <ColorGroup key={group.label} title={group.label} description={group.description}>
                    {group.roles.map((role) => (
                      <ColorField
                        key={role.key}
                        id={`theme-semantic-${role.key}`}
                        label={role.label}
                        hint={role.key}
                        fallback=""
                        placeholder={
                          group.unset === "derived"
                            ? "Worked out from your colors"
                            : "Built-in default"
                        }
                        value={theme.semantic?.[role.key] ?? ""}
                        onChange={(value) => setDraft(withSemanticColor(theme, role.key, value))}
                      />
                    ))}
                  </ColorGroup>
                ))}
              </div>
            </Disclosure>
            <Disclosure
              title="Rarity colors"
              summary={
                <span className="flex items-center gap-1">
                  {STOREFRONT_RARITY_TIERS.map((tier) => {
                    const color = theme.rarity?.[tier.key]?.trim() || tier.default;
                    return (
                      <span
                        key={tier.key}
                        title={`${tier.label} ${color}`}
                        className="size-3 rounded-full ring-1 ring-border"
                        style={{
                          backgroundColor: color.startsWith("#") ? color : `rgb(${color})`,
                        }}
                      />
                    );
                  })}
                </span>
              }
            >
              <p className="pb-4 text-xs leading-relaxed text-muted-foreground">
                The color of each tier on item cards, pulls and the opener. Each field shows the
                stock color until you set one; clear it to go back.
              </p>
              <div className="grid gap-4 @lg:grid-cols-2 @4xl:grid-cols-3">
                {STOREFRONT_RARITY_TIERS.map((tier) => (
                  <ColorField
                    key={tier.key}
                    id={`theme-rarity-${tier.key}`}
                    label={tier.label}
                    hint={`rarity ${tier.key}`}
                    fallback={tier.default}
                    value={theme.rarity?.[tier.key] ?? ""}
                    onChange={(value) =>
                      setDraft({ ...theme, rarity: { ...theme.rarity, [tier.key]: value } })
                    }
                  />
                ))}
              </div>
            </Disclosure>
          </div>
        </Card>

        <Card title="Type" description="The store's fonts, and how its headings are set.">
          <div className="grid gap-4">
            <FontField
              id="theme-font-family"
              label="Body font"
              defaultLabel="the shopper's system font"
              placeholder="Inter, sans-serif"
              value={theme.fontFamily ?? ""}
              onChange={(fontFamily) => setDraft({ ...theme, fontFamily })}
            />
            <FontField
              id="theme-heading-font-family"
              label="Heading font"
              defaultLabel="the body font"
              placeholder="Space Grotesk, sans-serif"
              value={theme.headingFontFamily ?? ""}
              onChange={(headingFontFamily) => setDraft({ ...theme, headingFontFamily })}
            />
            <FontField
              id="theme-mono-font-family"
              label="Numbers font"
              defaultLabel="the system's monospace font"
              placeholder="JetBrains Mono, monospace"
              value={theme.monoFontFamily ?? ""}
              onChange={(monoFontFamily) => setDraft({ ...theme, monoFontFamily })}
            />
          </div>
          <div className="border-t pt-4">
            {THEME_STYLES.filter((style) => style.card === "type").map((style) => (
              <StyleRow
                key={style.id}
                style={style}
                theme={theme}
                stored={config.theme}
                onChange={setDraft}
                palette={palette}
              />
            ))}
          </div>
        </Card>

        <Card title="Style" description="Shape, surfaces and motion: the feel of every page.">
          <div className="grid divide-y">
            {THEME_STYLES.filter((style) => style.card === "style").map((style) => (
              <StyleRow
                key={style.id}
                style={style}
                theme={theme}
                stored={config.theme}
                onChange={setDraft}
                palette={palette}
              />
            ))}
          </div>
        </Card>

        {changes.length > 0 && (
          // A raised card floating over the page's bottom edge, sliding in when the first change
          // lands, so an unsaved draft is hard to miss.
          <div className="sticky bottom-4 z-10 animate-in fade-in-0 slide-in-from-bottom-4 duration-200">
            <div className="grid gap-3 rounded-xl border border-primary/40 bg-popover px-4 py-3 shadow-2xl ring-1 ring-black/20 sm:px-5">
              {reviewing && (
                <ul className="grid max-h-64 gap-0.5 overflow-y-auto border-b pb-3 text-sm">
                  {changes.map((change) => (
                    <li key={change.path} className="flex items-center gap-3 py-1">
                      <span className="w-40 shrink-0 truncate font-medium">{change.label}</span>
                      <span className="min-w-0 flex-1 truncate text-muted-foreground">
                        <s>{change.before}</s> →{" "}
                        <span className="text-foreground">{change.after}</span>
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        aria-label={`Undo ${change.label}`}
                        onClick={() =>
                          setDraft(withFieldReverted(theme, config.theme, change.path))
                        }
                      >
                        Undo
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
              <div className="flex flex-wrap items-center gap-3">
                <span className="relative flex size-2.5 shrink-0" aria-hidden>
                  <span className="absolute inline-flex size-full animate-ping rounded-full bg-primary opacity-60 motion-reduce:hidden" />
                  <span className="relative inline-flex size-2.5 rounded-full bg-primary" />
                </span>
                <span className="text-sm font-medium">
                  {changes.length === 1 ? "1 unsaved change" : `${changes.length} unsaved changes`}
                </span>
                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  className="px-0"
                  aria-expanded={reviewing}
                  onClick={() => setReviewing((open) => !open)}
                >
                  {reviewing ? "Hide" : "Review"}
                </Button>
                <div className="ml-auto flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    disabled={update.isPending}
                    onClick={reset}
                  >
                    Discard
                  </Button>
                  <Button type="submit" disabled={update.isPending || !canEdit}>
                    {update.isPending ? "Saving…" : "Save theme"}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </form>

      <Dialog open={jsonOpen} onOpenChange={setJsonOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Import or export</DialogTitle>
            <DialogDescription>
              The whole theme as JSON: a backup, or a way to move it between environments.
            </DialogDescription>
          </DialogHeader>
          <ThemeJsonPanel
            theme={theme}
            onLoad={(loaded) => {
              setDraft(loaded);
              setJsonOpen(false);
            }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** A titled card holding a group of the Theme tab, with a line on what it holds. */
function Card({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: ReactNode;
}) {
  return (
    <section className="grid gap-5 rounded-xl border bg-card p-4 sm:p-5">
      <div className="grid gap-1">
        <h2 className="text-base font-semibold">{title}</h2>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
      {children}
    </section>
  );
}

/** A collapsed group: its title and a summary of what it holds, opening onto its fields. */
function Disclosure({
  title,
  summary,
  children,
}: {
  title: string;
  summary: ReactNode;
  children: ReactNode;
}) {
  return (
    // Kept mounted while closed, so the browser's find opens a group holding a match.
    <Collapsible className="group/disclosure">
      <CollapsibleTrigger className="flex w-full items-center gap-2 py-3 text-left text-sm">
        <ChevronRight className="size-4 shrink-0 transition-transform group-data-open/disclosure:rotate-90" />
        <span className="font-medium">{title}</span>
        <span className="ml-auto min-w-0 truncate text-xs text-muted-foreground">{summary}</span>
      </CollapsibleTrigger>
      <CollapsibleContent keepMounted hiddenUntilFound className="pb-5 pl-6">
        {children}
      </CollapsibleContent>
    </Collapsible>
  );
}

/** A labeled grid of color fields inside Worked-out colors. */
function ColorGroup({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: ReactNode;
}) {
  return (
    <div className="grid gap-3">
      <div className="grid gap-0.5">
        <h3 className="text-sm font-medium">{title}</h3>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
      <div className="grid gap-4 @lg:grid-cols-2 @4xl:grid-cols-3">{children}</div>
    </div>
  );
}

/**
 * One Style choice: its words as one control (Custom when the theme matches none), a line of
 * what its fields are set to, and Details, each field on its own.
 */
function StyleRow({
  style,
  theme,
  stored,
  palette,
  onChange,
}: {
  style: ThemeStyle;
  theme: StorefrontTheme;
  stored: StorefrontTheme;
  palette: { primary: string; "chart-2": string };
  onChange: (theme: StorefrontTheme) => void;
}) {
  const matched = matchedPreset(theme, style);
  const paths = new Set(stylePaths(style));
  const changed = listThemeChanges(stored, theme).some((change) => paths.has(change.path));
  const pick = (label: string) => {
    const preset = style.presets.find((entry) => entry.label === label);
    if (preset) onChange(withStylePicked(theme, stored, style, preset, palette));
  };
  const hasDetails = style.fields.length > 0 || style.radius || style.stageColors;
  const stageOn = (theme.stage?.recipe ?? "none") !== "none";
  const summary = styleSummary(theme, style);

  return (
    // One shrinkable column, so a long Details summary truncates instead of widening the card.
    <div className="grid grid-cols-[minmax(0,1fr)] gap-3 py-4 first:pt-0 last:pb-0">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <div className="grid min-w-40 flex-1 gap-0.5">
          <h3 className="flex items-center gap-2 text-sm font-medium">
            {style.title}
            {matched === null && <Badge variant="secondary">Custom</Badge>}
            {changed && (
              <span className="size-1.5 rounded-full bg-primary" title="Unsaved" aria-hidden />
            )}
          </h3>
          <p className="text-xs text-muted-foreground">{style.description}</p>
        </div>
        <ChoiceField
          id={`theme-style-${style.id}`}
          label={style.title}
          hideLabel
          options={style.presets.map((preset) => ({ value: preset.label, label: preset.label }))}
          value={matched?.label ?? ""}
          onChange={pick}
        />
      </div>
      {hasDetails && (summary !== "" || style.stageColors) && (
        <Collapsible className="group/details">
          <CollapsibleTrigger className="flex min-w-0 max-w-full items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground">
            <ChevronRight className="size-3.5 transition-transform group-data-open/details:rotate-90" />
            <span>Details</span>
            {summary && <span className="truncate">· {summary}</span>}
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-3 grid gap-5 rounded-lg bg-muted/40 p-4 @lg:grid-cols-2">
            {style.radius && (
              <RadiusField
                value={theme.radius ?? ""}
                onChange={(radius) => onChange({ ...theme, radius })}
              />
            )}
            {style.fields.map((choice) => (
              <ChoiceField
                key={`${choice.group}.${choice.field}`}
                id={`theme-${choice.group}-${choice.field}`}
                label={choice.label}
                options={choice.options}
                value={choiceValue(theme, choice)}
                onChange={(value) => onChange(withChoice(theme, choice, value))}
              />
            ))}
            {style.stageColors &&
              (stageOn ? (
                Array.from({ length: MAX_STAGE_COLORS }, (_, index) => (
                  <ColorField
                    // biome-ignore lint/suspicious/noArrayIndexKey: the slots are fixed positions
                    key={index}
                    id={`theme-stage-color-${index + 1}`}
                    label={`Color ${index + 1}`}
                    hint={`stage ${index + 1}`}
                    fallback=""
                    placeholder="Unused"
                    value={theme.stage?.colors?.[index] ?? ""}
                    onChange={(value) => onChange(withStageColor(theme, index, value))}
                  />
                ))
              ) : (
                <p className="text-xs text-muted-foreground">
                  Pick an effect to choose the colors it is drawn in.
                </p>
              ))}
          </CollapsibleContent>
        </Collapsible>
      )}
    </div>
  );
}

/**
 * A whole theme as JSON, for a backup or to move it between a store's environments (and, for
 * platform staff, between tenants): Copy and Download export what the editor holds now (as a save
 * would store it); a pasted theme, checked by the save's own schema, replaces the draft, so it
 * shows in every group and nothing is stored until Save.
 */
function ThemeJsonPanel({
  theme,
  onLoad,
}: {
  theme: StorefrontTheme;
  onLoad: (theme: StorefrontTheme) => void;
}) {
  const [text, setText] = useState("");
  const [error, setError] = useState<string | null>(null);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(themeToJson(theme));
      toast.success("Theme JSON copied.");
    } catch {
      toast.error("The browser blocked the clipboard. Use Download instead.");
    }
  };

  const download = () => {
    const url = URL.createObjectURL(new Blob([themeToJson(theme)], { type: "application/json" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = "storefront-theme.json";
    link.click();
    URL.revokeObjectURL(url);
  };

  const load = () => {
    const result = parseThemeJson(text);
    if (!result.ok) {
      setError(result.message);
      return;
    }
    onLoad(result.theme);
    setText("");
    setError(null);
    toast.success("Theme loaded into the draft. Review it, then save.");
  };

  return (
    <div className="grid gap-6">
      <div className="grid gap-2">
        <span className="text-sm font-medium">Export</span>
        <p className="text-xs text-muted-foreground">
          The theme as the editor holds it now, unsaved changes included. Keep it as a backup, or
          load it into a sandbox to try changes there first.
        </p>
        <div className="flex gap-2">
          <Button type="button" variant="outline" size="sm" onClick={() => void copy()}>
            Copy JSON
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={download}>
            Download
          </Button>
        </div>
      </div>
      <div className="grid gap-2">
        <Label htmlFor="theme-json-import">Import</Label>
        <p className="text-xs text-muted-foreground">
          Paste a theme you exported, from this storefront or one of its sandboxes. It replaces
          every theme setting in the draft; nothing is stored until you save.
        </p>
        <Textarea
          id="theme-json-import"
          value={text}
          onChange={(event) => {
            setText(event.target.value);
            setError(null);
          }}
          placeholder='{ "tokens": { "primary": "#22c55e" }, … }'
          spellCheck={false}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? "theme-json-error" : undefined}
          className="min-h-40 font-mono text-xs"
        />
        {error && (
          <p id="theme-json-error" className="text-xs text-destructive">
            {error}
          </p>
        )}
        <div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={text.trim() === ""}
            onClick={load}
          >
            Load into draft
          </Button>
        </div>
      </div>
    </div>
  );
}
